import { map, pipe, toArray } from '@fxts/core';
import { MembershipJoinWidget } from '@marpple/mco-membership-ui';
import { $, html, type Html, Page, View } from 'rune-ts';
import { MarketplaceAuthF } from '../../../../../modules/Marketplace/Auth/F/Function/module/MarketplaceAuthF';
import { MShopUtilF } from '../../../../../modules/MShop/Util/F/Function/module/MShopUtilF';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { MembershipErrorEnable } from '../../../features/Membership/error/error_enable';
import { MembershipUtilS } from '../../../features/Membership/outbound/share';
import { onContactButtonClick } from '../../../features/Membership/util/contact';
import { getMembershipFaqUrl } from '../../../features/Membership/util/makeUrl';
import {
  generateCreatorMembershipTab,
  MembershipIdType,
} from '../../../features/ProductList/constant/Membership/tabs';
import { makeEmptyScreenNavigate } from '../../../shared/app/navigate';
import { navigateTo } from '../../../shared/app/util';
import { htmlIf } from '../../../shared/util';
import { autoBind } from '../../../shared/util/autoBind';
import { goLogin } from '../../../shared/util/goLogin';
import { getSharedData } from '../../../shared/util/rune';
import { makePlusUrl } from '../../../shared/util/url';
import { footer_links } from '../../components/cells/Footer/constant';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { SubHeaderMobile } from '../../components/cells/Header/Global/views/SubHeaderMobile.ts';
import { Tabs } from '../../components/cells/Tabs/Tabs';
import klass from './CreatorShopMembership.module.scss';
import { getActivatedSdkWithEvent } from '../../../features/Membership/util';
import { ConfigSentryF } from '../../../../../modules/Config/Sentry/F/Function/module/ConfigSentryF';
import { MEMBERSHIP, MEMBERSHIP_UI_IDENTIFIER } from '../../../features/Membership/constant';
import { Title } from '../../components/atoms/Title/Title';

export type CreatorShopMembershipPageData = {
  type: MembershipIdType;
  layout_options: BaseLayoutOptions;
  membership_project_id: string;
  membership_token?: string;
  domain_name: string;
  plus_domain_name: string;
  support_selected_tab_index?: number;
};

class CreatorShopMembershipBodyView extends View<CreatorShopMembershipPageData> {
  private _membershipErrorEnable = new MembershipErrorEnable(this);

  protected override template(): Html {
    const { is_mobile } = this.data.layout_options;
    const tab_helper = generateCreatorMembershipTab(this.data.domain_name, this.data.type);

    return html` <div class="${klass.page} ${htmlIf(klass.support, this.data.type === 'support')}">
      ${new Title({
        sub_title: ET('mps2::membership::main::description'),
        size: is_mobile ? 'large' : 'xLarge',
        title: ET('mps2::membership::main::title'),
        custom_klass: klass.title,
      })}
      <div class="${klass.tabs}">
        ${new Tabs({
          tabs: pipe(
            tab_helper.getSubTabsById('membership'),
            map((tab) => ({ ...tab, klass: klass.tab })),
            toArray,
          ),
          current_tab_id: this.data.type,
        })}
      </div>
      <div class="${MEMBERSHIP_UI_IDENTIFIER} ${klass.ui}"></div>
    </div>`;
  }

  protected override async onRender() {
    const ui_section = $(this.element()).find(`.${MEMBERSHIP_UI_IDENTIFIER}`)?.element();
    try {
      if (ui_section) {
        const client = await getActivatedSdkWithEvent(this.data.membership_token);
        const widget = new MembershipJoinWidget({
          client,
          toggleSpinner: MembershipUtilS.toggleSpinner,
          isApp: MShopUtilF.isApp(),
          isMobile: this.data.layout_options.is_mobile,
          projectId: this.data.membership_project_id,
          onJoinMembershipButtonClick: this._goJoinMembership,
          onSeeMyMembershipRewardsButtonClick: (membership_id) => {
            this._openLink(`/${ET.lang}/@/my_memberships/detail/${membership_id}`);
          },
          onGoToExploreProductsButtonClick: () => {
            this._openLink(
              makePlusUrl(this.data.plus_domain_name, `/${ET.lang}/${this.data.domain_name}/products`),
            );
          },
          onDimThumbnailClick: () => {
            this._openLink(
              makePlusUrl(
                this.data.plus_domain_name,
                `/${ET.lang}/${this.data.domain_name}/membership/products`,
              ),
            );
          },
          onContactButtonClick: onContactButtonClick({
            type: MEMBERSHIP,
          }),
          onGoToSeeMembershipFaqButtonClick: () => {
            navigateTo(getMembershipFaqUrl(), makeEmptyScreenNavigate);
          },
          onError: this._membershipErrorEnable.onError,
          onScrollLock: MShopUtilF.bodyFixed$,
        });
        widget.render(ui_section);
      }
    } catch (e) {
      ConfigSentryF.error(e);
      await this._membershipErrorEnable.openWidgetRenderErrorModal();
    }
  }

  private _openLink(link: string) {
    navigateTo(link, makeEmptyScreenNavigate);
  }

  @autoBind()
  private _goJoinMembership(membership_id: string) {
    if (!MarketplaceAuthF.isSignedIn()) {
      return goLogin();
    }
    return this._openLink(
      makePlusUrl(
        this.data.plus_domain_name,
        `/${ET.lang}/${this.data.domain_name}/membership/payment?membership_id=${membership_id}`,
      ),
    );
  }
}

export class CreatorShopMembershipPage extends Page<CreatorShopMembershipPageData> {
  protected override template(): Html {
    const sharedData = getSharedData(this);
    const { layout_options } = this.data;

    return html`
      <div>
        ${new LayoutView(
          {
            body: new CreatorShopMembershipBodyView(this.data),
            footer_data: { ...footer_links, is_mobile: layout_options.is_mobile, lang: ET.lang },
          },
          {
            full_width: true,
            has_bottom_tab_bar: !sharedData?.webviewapp,
            selected_bottom_tab_idx: -1,
            ...layout_options,
          },
        )}
      </div>
    `;
  }

  override onMount() {
    super.onMount();
    SubHeaderMobile.handleChangeMenuOutside();
  }
}
